import { Component, Inject } from "@angular/core";
import { FeaturePermissionName } from "@common/ADAPT.Common.Model/embed/feature-permission-name.enum";
import { MeetingAgendaTemplate } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-template";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { ErrorHandlingUtilities } from "@common/lib/utilities/error-handling-utilities";
import { ADAPT_DIALOG_DATA } from "@common/ux/adapt-common-dialog/adapt-common-dialog.globals";
import { BaseDialogComponent } from "@common/ux/adapt-common-dialog/base-dialog.component/base-dialog.component";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { lastValueFrom } from "rxjs";
import { MeetingsService } from "../meetings.service";

export interface IMoveMeetingTemplateDialogData {
    template: MeetingAgendaTemplate;
    isCopying?: boolean;
}

@Component({
    selector: "adapt-move-meeting-template-dialog",
    templateUrl: "./move-meeting-template-dialog.component.html",
    styleUrl: "./move-meeting-template-dialog.component.css",
    standalone: false,
})
export class MoveMeetingTemplateDialogComponent extends BaseDialogComponent<IMoveMeetingTemplateDialogData, MeetingAgendaTemplate | undefined> {
    public readonly dialogName = "MoveMeetingTemplateDialog";

    public moveDestinations: (Team | null)[] = [];
    public template: MeetingAgendaTemplate;

    public constructor(
        @Inject(ADAPT_DIALOG_DATA) public dialogData: IMoveMeetingTemplateDialogData,
        private meetingsService: MeetingsService,
        private authService: AuthorisationService,
    ) {
        super();
        this.template = dialogData.template;
    }

    public get haveMoveDestinations() {
        return this.moveDestinations.length > 0;
    }

    @Autobind
    public filter(team: Team | null) {
        return (this.dialogData.isCopying || team?.teamId != this.template.teamId) &&
            this.authService.currentPersonHasPermission(FeaturePermissionName.OrganisationTeamConfigure, team ?? undefined);
    }

    @Autobind
    public async processMoveOrCopy() {
        try {
            this.setErrorMessage(undefined);
            let firstChangedTemplate: MeetingAgendaTemplate | undefined;
            if (this.moveDestinations.length > 0) {
                if (this.dialogData.isCopying) {
                    // template will be copied to the destinations
                    for (const team of this.moveDestinations) {
                        const result = await lastValueFrom(this.meetingsService.copyMeetingTemplateToTeam(this.template, team?.teamId));
                        if (!firstChangedTemplate) {
                            firstChangedTemplate = result;
                        }
                    }
                } else {
                    // change the teamId of the template to the first destination
                    this.template.teamId = this.moveDestinations[0]?.teamId;
                    await lastValueFrom(this.meetingsService.saveEntities([this.template]));
                    firstChangedTemplate = this.template;

                    // template will be copied to the remaining teams
                    for (const team of this.moveDestinations.slice(1)) {
                        await lastValueFrom(this.meetingsService.copyMeetingTemplateToTeam(this.template, team?.teamId));
                    }
                }
            }

            this.resolve(firstChangedTemplate);
        } catch (error) {
            this.setErrorMessage(ErrorHandlingUtilities.getHttpResponseMessage(error));
        }

    }
}
